import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from './error.service';
import { ToastrService } from 'ngx-toastr';
import { ENVIRONMENT_TYPE, SERVER_UP_CHECK_URL } from '@shared/config';
import { SKIP_AUTH } from '@shared/auth';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private injector = inject(Injector);
  private http = inject(HttpClient);
  private router = inject(Router);

  private remoteErrorLogger: ErrorHandler | null;
  private serverUpCheckUrl: string;
  private environmentType: string;

  constructor() {
    this.serverUpCheckUrl = inject(SERVER_UP_CHECK_URL);
    this.environmentType = inject(ENVIRONMENT_TYPE);
    this.remoteErrorLogger = Sentry.createErrorHandler({ showDialog: true });
  }

  handleError(error: Error | HttpErrorResponse) {
    const errorService = this.injector.get(ErrorService);
    const toastr = this.injector.get(ToastrService);
    let message;

    // Server Error
    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) {
        // 403 toastr already shown by server error interceptor
        this.logErrorToSentry(error);
        return;
      }

      // most browsers will not pass info about options/network errors to angular, so status for 503 error will be 0
      if (!error.status || error.status === 503) {
        if (error.url?.includes(this.serverUpCheckUrl)) {
          if (window.location.href.includes('503')) {
            return;
          }
          this.router.navigate(['/503']);
          return;
        }
        this.http.get(this.serverUpCheckUrl, { context: new HttpContext().set(SKIP_AUTH, true) }).subscribe(() => {
          toastr.error('Try again later, and check your firewall settings.', 'Network error', {
            onActivateTick: true
          });
        });
        return;
      }

      message = errorService.getServerMessage(error);
      toastr.error(message, 'Server Error', {
        onActivateTick: true
      });
      this.logErrorToSentry(error);
      return;
    }

    // Client Error
    if (
      error.message.includes('Failed to fetch dynamically imported module') || // chrome
      error.message.includes('error loading dynamically imported module') || // firefox
      error.message.includes('Load failed for module') // safari
    ) {
      toastr.error('Please reload the page.', 'Software updated', {
        onActivateTick: true
      });
      return;
    }
    message = errorService.getClientMessage(error);
    toastr.error(message, 'Error', {
      onActivateTick: true
    });
    this.logErrorToSentry(error);
  }

  logErrorToSentry(error: Error | HttpErrorResponse) {
    console.error(error); // used to verify sentry logging in error.cy.ts

    if (!['QA', 'PROD'].includes(this.environmentType)) {
      return;
    }

    Sentry.withScope((scope) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tags = (error as any)?.sentryTags;
      if (Array.isArray(tags) && tags.length > 0) {
        tags.forEach((tag: { key: string; value: string }) => {
          scope.setTag(tag.key, tag.value);
        });
      }

      if (error instanceof HttpErrorResponse) {
        const fingerprint = this.generateHttpErrorFingerprint(error as HttpErrorResponse);
        scope.setFingerprint(fingerprint);
      }

      this.remoteErrorLogger!.handleError(error);
    });
  }

  private generateHttpErrorFingerprint(error: HttpErrorResponse): string[] {
    const baseFingerprint = ['http-error', error.status.toString(), error.statusText || 'unknown'];

    if (error.url) {
      try {
        const url = new URL(error.url);
        const pathSegments = url.pathname.split('/').filter((segment) => segment.length > 0);
        if (pathSegments.length > 0) {
          baseFingerprint.push(`path-${pathSegments.join('-')}`);
        }
      } catch (e) {
        // If URL parsing fails, use the full URL as a fallback
        baseFingerprint.push(`url-${error.url.replace(/[^a-zA-Z0-9]/g, '-')}`);
      }
    }

    return baseFingerprint;
  }
}
